import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Loader2, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SALE_LISTING_STATUSES } from "@shared/schema";
import type { SaleListingStatus } from "@shared/schema";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AdminSaleListing, SaleListingFormState } from "./types";
import { listingToForm } from "./helpers";

export function SaleListingsAdminPanel({ canManage = true }: { canManage?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SaleListingFormState>({
    askingPrice: "", status: "active", description: "", sellerName: "", contactEmail: "", contactPhone: "", photos: "",
  });

  const listingsQuery = useQuery<AdminSaleListing[]>({
    queryKey: ["/api/admin/sale-listings"],
    queryFn: async () => {
      const res = await fetch("/api/admin/sale-listings", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 10_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/sale-listings"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/sell/marketplace"] });
  };

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/admin/sale-listings/${id}`, payload),
    onSuccess: () => {
      invalidate();
      toast({ title: "Listing updated" });
      setEditorOpen(false);
    },
    onError: (e) => toast({ title: "Failed to update listing", description: parseErrorMessage(e, "Couldn't update listing. Please try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/admin/sale-listings/${id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Listing removed" });
    },
    onError: (e) => toast({ title: "Failed to delete listing", description: parseErrorMessage(e, "Couldn't delete listing. Please try again."), variant: "destructive" }),
  });

  const hideMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      apiRequest("POST", `/api/admin/sale-listings/${id}/hide`, { reason }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Listing hidden", description: "It is now removed from the public marketplace." });
    },
    onError: (e) => toast({ title: "Failed to hide listing", description: parseErrorMessage(e, "Couldn't hide listing. Please try again."), variant: "destructive" }),
  });

  const [listingToUnhide, setListingToUnhide] = useState<AdminSaleListing | null>(null);
  const [listingToDelete, setListingToDelete] = useState<AdminSaleListing | null>(null);

  const unhideMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/admin/sale-listings/${id}/unhide`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Listing unhidden", description: "It is visible in the public marketplace again." });
    },
    onError: (e) => toast({ title: "Failed to unhide listing", description: parseErrorMessage(e, "Couldn't unhide listing. Please try again."), variant: "destructive" }),
  });

  const handleToggleHidden = (l: AdminSaleListing) => {
    if (l.hidden) {
      setListingToUnhide(l);
      return;
    }
    const reason = window.prompt(`Reason for hiding "${l.propertyName}"? This is captured for audit and shown to other admins.`);
    if (reason == null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      toast({ title: "Moderation reason required", description: "Enter a short reason so other admins know why this listing was hidden.", variant: "destructive" });
      return;
    }
    hideMutation.mutate({ id: l.id, reason: trimmed });
  };

  const listings = listingsQuery.data ?? [];

  const openEdit = (l: AdminSaleListing) => {
    setEditingId(l.id);
    setForm(listingToForm(l));
    setEditorOpen(true);
  };

  const handleSave = () => {
    if (editingId == null) return;
    const priceNum = Number(form.askingPrice);
    if (!form.askingPrice.trim() || !Number.isFinite(priceNum) || priceNum <= 0) {
      toast({ title: "Invalid asking price", description: "Asking price must be greater than 0.", variant: "destructive" });
      return;
    }
    const photos = form.photos.split("\n").map((s) => s.trim()).filter(Boolean);
    const payload: Record<string, unknown> = {
      askingPrice: priceNum,
      status: form.status,
      description: form.description.trim() || null,
      sellerName: form.sellerName.trim() || null,
      contactEmail: form.contactEmail.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      photos,
    };
    updateMutation.mutate({ id: editingId, payload });
  };

  const handleDelete = (l: AdminSaleListing) => {
    setListingToDelete(l);
  };

  const setField = <K extends keyof SaleListingFormState>(key: K, value: SaleListingFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Sell Marketplace Listings</h2>
          <p className="text-sm text-muted-foreground">
            Moderate every seller-created sale listing. Edit details, change status, or remove off-brand or low-quality listings.
          </p>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="text-sale-listings-summary">
          {listings.length} listing{listings.length !== 1 ? "s" : ""}
        </span>
      </div>

      {listingsQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (<Skeleton key={i} className="h-12 w-full" />))}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">No sale listings yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Asking Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listings.map((l) => (
                <TableRow key={l.id} data-testid={`row-sale-listing-${l.id}`}>
                  <TableCell className="font-medium">
                    <div data-testid={`text-listing-property-${l.id}`}>{l.propertyName}</div>
                    {l.propertyAddress && (
                      <div className="text-xs text-muted-foreground">{l.propertyAddress}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div data-testid={`text-listing-seller-${l.id}`}>{l.sellerName || "—"}</div>
                    {l.sellerEmail && (
                      <div className="text-xs text-muted-foreground">{l.sellerEmail}</div>
                    )}
                  </TableCell>
                  <TableCell>${Number(l.askingPrice ?? 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant="outline" data-testid={`badge-listing-status-${l.id}`}>{l.status}</Badge>
                      {l.hidden && (
                        <Badge variant="destructive" data-testid={`badge-listing-hidden-${l.id}`}>Hidden</Badge>
                      )}
                      {l.hidden && l.hiddenReason && (
                        <span className="text-xs text-muted-foreground" data-testid={`text-listing-hidden-reason-${l.id}`}>
                          “{l.hiddenReason}”
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(l.createdAt as unknown as string).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant={l.hidden ? "default" : "outline"}
                          onClick={() => handleToggleHidden(l)}
                          disabled={hideMutation.isPending || unhideMutation.isPending}
                          data-testid={`button-toggle-hidden-listing-${l.id}`}
                          title={l.hidden ? "Unhide listing (show in marketplace)" : "Hide listing from marketplace"}
                        >
                          {l.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(l)} data-testid={`button-edit-listing-${l.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(l)} data-testid={`button-delete-listing-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">View only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Sale Listing</DialogTitle>
            <DialogDescription>
              Moderate this seller-created listing. Changes are saved immediately and reflected in the marketplace.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Asking Price *</Label>
              <Input value={form.askingPrice} onChange={(e) => setField("askingPrice", e.target.value)} placeholder="225000" data-testid="input-listing-asking-price" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v as SaleListingStatus)}>
                <SelectTrigger data-testid="select-listing-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SALE_LISTING_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Seller Name</Label>
              <Input value={form.sellerName} onChange={(e) => setField("sellerName", e.target.value)} data-testid="input-listing-seller-name" />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input value={form.contactEmail} onChange={(e) => setField("contactEmail", e.target.value)} data-testid="input-listing-contact-email" />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={form.contactPhone} onChange={(e) => setField("contactPhone", e.target.value)} data-testid="input-listing-contact-phone" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} rows={4} data-testid="textarea-listing-description" />
            </div>
            <div className="md:col-span-2">
              <Label>Photos (one URL per line)</Label>
              <Textarea
                value={form.photos}
                onChange={(e) => setField("photos", e.target.value)}
                rows={4}
                placeholder={"https://example.com/a.jpg\nhttps://example.com/b.jpg"}
                data-testid="textarea-listing-photos"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="button-cancel-listing">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              data-testid="button-save-listing"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!listingToUnhide} onOpenChange={(v) => !v && setListingToUnhide(null)}>
        <AlertDialogContent data-testid="dialog-unhide-listing-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Unhide listing?</AlertDialogTitle>
            <AlertDialogDescription>
              {listingToUnhide
                ? `Unhide listing for "${listingToUnhide.propertyName}"? It will appear in the public marketplace again.`
                : "It will appear in the public marketplace again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-unhide-listing">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-unhide-listing"
              disabled={unhideMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!listingToUnhide) return;
                const id = listingToUnhide.id;
                unhideMutation.mutate(id, {
                  onSettled: () => setListingToUnhide(null),
                });
              }}
            >
              {unhideMutation.isPending ? "Unhiding..." : "Unhide"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!listingToDelete} onOpenChange={(v) => !v && setListingToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-listing-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove listing?</AlertDialogTitle>
            <AlertDialogDescription>
              {listingToDelete
                ? `Remove listing for "${listingToDelete.propertyName}"? This cannot be undone.`
                : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-listing">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-listing"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!listingToDelete) return;
                const id = listingToDelete.id;
                deleteMutation.mutate(id, {
                  onSettled: () => setListingToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
